import React, { useContext, useEffect, useMemo, useRef } from "react";
import { t } from "@lingui/macro";
import Loading from "../../../components/Loading/Loading";
import { LoginContext } from "../../../components/LoginProvider/LoginProvider";
import { SEOReactHelmet } from "../../../components/SEO/SEOReactHelmet";
import { SignUpFlowLocationType } from "../types/SignUpFlowLocationType";
import { PaymentError } from "../components/PaymentError";
import { PaymentMethodSelect } from "../components/PaymentMethodSelect";
import StripeSubscribeSection from "../components/StripeSubscribeSection";
import PayPalSubscribeSection from "../components/PayPalSubscribeSection";
import { PaymentProvider } from "../contexts/PaymentProvider";
import { isBrowser } from "../../../utils/ssr";
import { GatsbyImage, withArtDirection } from "gatsby-plugin-image";
import { graphql, navigate as gatsbyNavigate, useStaticQuery } from "gatsby";
import { getLocale } from "../../../utils/getLocale";
import { Locale } from "../../../types/Locale";
import { useNavigate } from "../../../components/Link/Link";
import { LoginPage } from "../../../pages";

// Discounted page for English users
const SubscriptionPaymentPageV3 = ({ location }: { location?: SignUpFlowLocationType }) => {
  const imageData = useStaticQuery(graphql`
    query {
      desktop: file(relativePath: { eq: "sign-up-flow/egg-background-desktop.jpg" }) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED, width: 768)
        }
      }
      mobile: file(relativePath: { eq: "sign-up-flow/egg-background-mobile.jpg" }) {
        childImageSharp {
          gatsbyImageData(layout: CONSTRAINED, width: 640)
        }
      }
    }
  `);

  const imageSources = withArtDirection(imageData.mobile?.childImageSharp?.gatsbyImageData, [
    {
      media: "(min-width: 768px)",
      image: imageData.desktop?.childImageSharp?.gatsbyImageData,
    },
  ]);

  const tt = useTranslations();
  const { loading: loginLoading, user, loggedIn } = useContext(LoginContext);
  const paymentDetailsRef = useRef<HTMLDivElement>(null);

  const locale = useMemo(() => getLocale(), []);

  const queryParams = useMemo(() => location?.search || "", [location]);

  // Custom hook for custom navigate function
  const navigate = useNavigate();

  // Handle discount param
  useEffect(() => {
    if (locale !== Locale.EN) {
      gatsbyNavigate(`../payment${queryParams}`, {
        replace: true,
      });
    }
  }, [locale, loggedIn, loginLoading, queryParams, user]);

  const windowPath = isBrowser() && window.location.pathname;

  // Handle logged out user
  useEffect(() => {
    if (!loginLoading && !user && !loggedIn) {
      navigate({
        to: LoginPage,
        query: {
          redirectTo: `${isBrowser() ? window.location.origin : process.env.GATSBY_WEBSITE_URL}${
            windowPath ?? ""
          }${location?.search ?? ""}`,
        },
      });
    }
  }, [location?.search, loggedIn, loginLoading, navigate, user, windowPath]);

  useEffect(() => {
    if (isBrowser()) {
      window.localStorage.setItem("selectedPlan", JSON.stringify("ANNUAL"));
    }
  }, []);

  // Full screen loading
  if (loginLoading) {
    return (
      <div className="bg-white min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <>
      <SEOReactHelmet title={tt.paymentSEOTitle || "Payment"} noindex />
      <PaymentProvider>
        <main className="flex flex-col md:flex-row overflow-hidden min-h-screen">
          <section className="relative overflow-y-hidden pb-16 md:w-1/2 md:min-w-[26rem] pt-12 md:pt-24">
            <div className="relative z-10 text-white md:px-8 mx-auto w-fit">
              <div className="flex flex-col items-center md:items-start">
                {/* Diet Doctor Logo */}
                <svg
                  className="h-11 md:h-16"
                  viewBox="0 0 115 44"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <g clipPath="url(#prefix__clip0_1600_2247)" fill="#fff">
                    <path d="M0 44v-5.5h11.054c4.397 0 8.615-1.738 11.724-4.833A16.46 16.46 0 0027.634 22a16.46 16.46 0 00-4.856-11.667A16.622 16.622 0 0011.054 5.5H0V0h11.054a22.162 22.162 0 0115.632 6.444A21.946 21.946 0 0133.161 22c0 5.835-2.33 11.43-6.475 15.556A22.162 22.162 0 0111.054 44H0zm11.054-33c2.931 0 5.743 1.159 7.816 3.222A10.973 10.973 0 0122.107 22c0 2.917-1.164 5.715-3.237 7.778A11.081 11.081 0 0111.054 33H0V11h11.054zM5.527 27.5h5.527a5.54 5.54 0 003.908-1.61A5.487 5.487 0 0016.58 22a5.487 5.487 0 00-1.62-3.89 5.54 5.54 0 00-3.907-1.61H5.527v11zM52.045 6.472a6.78 6.78 0 012.823 2.695 8.003 8.003 0 011.004 4.051 7.88 7.88 0 01-1.004 4.03 6.817 6.817 0 01-2.823 2.653 9.207 9.207 0 01-4.27.944h-5.402V5.528h5.379a9.208 9.208 0 014.293.944zm-.627 10.445c.878-.874 1.319-2.11 1.322-3.708.003-1.598-.438-2.846-1.322-3.744-.884-.899-2.134-1.348-3.749-1.348h-2.21v10.116h2.187c1.635 0 2.865-.436 3.75-1.316h.022zM58.396 6.724a1.804 1.804 0 01-.398-1.979c.092-.22.227-.42.398-.588a2.032 2.032 0 012.712 0 1.804 1.804 0 01.399 1.979c-.093.22-.228.42-.398.588a2.046 2.046 0 01-2.713 0zm2.86 1.984v12.137h-3.09V8.708h3.09zM75.584 15.689h-8.93c.027.78.357 1.52.921 2.062.546.5 1.266.768 2.008.747.51.03 1.017-.095 1.454-.357a2.52 2.52 0 00.996-1.114h3.326a5.522 5.522 0 01-2.027 2.887 5.956 5.956 0 01-3.684 1.128 6.371 6.371 0 01-3.165-.78 5.454 5.454 0 01-2.183-2.204 6.744 6.744 0 01-.783-3.29 6.849 6.849 0 01.779-3.31 5.347 5.347 0 012.16-2.195 6.935 6.935 0 016.314-.028 5.298 5.298 0 012.137 2.118 6.36 6.36 0 01.765 3.153c.004.396-.025.792-.088 1.183zm-3.109-2.063a2.41 2.41 0 00-.86-1.897A3.04 3.04 0 0069.545 11a2.799 2.799 0 00-1.93.692 3.02 3.02 0 00-.958 1.92l5.817.014zM81.521 11.21v5.886a1.144 1.144 0 00.295.884c.29.211.647.31 1.004.275h1.437v2.59h-1.944c-2.6 0-3.902-1.257-3.905-3.772V11.21h-1.456V8.708h1.456v-3.03h3.113v3.03h2.736v2.526l-2.736-.023zM52.044 24.159a6.766 6.766 0 012.847 2.69 7.986 7.986 0 011.004 4.047 7.88 7.88 0 01-1.004 4.029 6.799 6.799 0 01-2.846 2.658 9.248 9.248 0 01-4.27.945h-5.402V23.215h5.379a9.25 9.25 0 014.292.944zm-.626 10.45c.878-.88 1.319-2.118 1.322-3.713.003-1.595-.438-2.841-1.322-3.74-.884-.916-2.134-1.375-3.749-1.375h-2.21v10.12h2.187c1.618.015 2.868-.415 3.75-1.292h.022zM60.436 37.955a5.662 5.662 0 01-2.22-2.205 6.621 6.621 0 01-.801-3.295 6.424 6.424 0 01.824-3.291 5.73 5.73 0 012.262-2.205 6.936 6.936 0 016.397 0 5.732 5.732 0 012.261 2.205 6.469 6.469 0 01.848 3.29 6.319 6.319 0 01-.848 3.296 5.906 5.906 0 01-2.303 2.205c-.991.525-2.1.793-3.224.779a6.473 6.473 0 01-3.196-.78zm4.749-2.292a2.945 2.945 0 001.183-1.22c.316-.61.474-1.288.46-1.975a3.65 3.65 0 00-.92-2.667 3.025 3.025 0 00-2.239-.917 2.971 2.971 0 00-2.22.917 3.715 3.715 0 00-.889 2.667 3.751 3.751 0 00.87 2.668 2.865 2.865 0 002.193.916 3.2 3.2 0 001.562-.403v.014zM72.236 29.155a5.461 5.461 0 012.137-2.196 6.187 6.187 0 013.132-.779 6.064 6.064 0 013.763 1.128 5.497 5.497 0 011.994 3.171h-3.33a2.402 2.402 0 00-.893-1.237 2.593 2.593 0 00-1.557-.459 2.549 2.549 0 00-2.091.958 4.201 4.201 0 00-.778 2.714c-.06.958.213 1.908.773 2.69a2.53 2.53 0 002.091.953 2.349 2.349 0 001.525-.398c.447-.304.774-.753.925-1.27h3.33a5.589 5.589 0 01-2.008 3.14 5.933 5.933 0 01-3.749 1.164 6.237 6.237 0 01-3.132-.78 5.512 5.512 0 01-2.137-2.195 7.436 7.436 0 010-6.604h.005zM89.13 28.898v5.885a1.163 1.163 0 00.294.89c.29.21.647.308 1.005.274h1.437v2.59h-1.944c-2.604 0-3.906-1.26-3.906-3.777v-5.862h-1.455v-2.52h1.455v-3.003h3.114v3.007h2.736v2.52l-2.736-.004zM96.342 37.955a5.66 5.66 0 01-2.22-2.205 6.62 6.62 0 01-.801-3.295 6.424 6.424 0 01.824-3.291 5.732 5.732 0 012.262-2.205 6.936 6.936 0 016.397 0 5.695 5.695 0 012.257 2.205 6.427 6.427 0 01.829 3.29 6.32 6.32 0 01-.848 3.296 5.905 5.905 0 01-2.302 2.205 6.73 6.73 0 01-3.224.779 6.474 6.474 0 01-3.174-.78zm4.749-2.292a2.941 2.941 0 001.183-1.22 4.127 4.127 0 00.438-1.975 3.642 3.642 0 00-.926-2.676 3.022 3.022 0 00-2.238-.917 2.97 2.97 0 00-2.22.917 3.715 3.715 0 00-.89 2.667 3.751 3.751 0 00.871 2.668 2.865 2.865 0 002.193.916 3.237 3.237 0 001.589-.394v.014zM112.803 26.748a4.341 4.341 0 012.197-.55v3.209h-.815a3.143 3.143 0 00-2.197.683c-.494.455-.74 1.245-.737 2.37v6.058h-3.09v-12.14h3.09v1.883a4.363 4.363 0 011.552-1.513z" />
                  </g>
                  <defs>
                    <clipPath id="prefix__clip0_1600_2247">
                      <path fill="#fff" d="M0 0h115v44H0z" />
                    </clipPath>
                  </defs>
                </svg>

                {/* Heading */}
                <h2 className="mt-8 font-medium text-[2.5rem] md:text-6xl">Get 45% off</h2>

                {/* Three USP */}
                <ul className="list-none p-0 m-0 mt-4 flex flex-col">
                  <li className="flex flex-row items-center space-x-4">
                    <WhiteCheckMark />
                    <span className="mt-1 text-base md:text-xl">Reach your weight and health goals</span>
                  </li>
                  <li className="flex flex-row items-center space-x-4 mt-1">
                    <WhiteCheckMark />
                    <span className="mt-1 text-base md:text-xl">Discover what foods work for you</span>
                  </li>
                  <li className="flex flex-row items-center space-x-4 mt-1">
                    <WhiteCheckMark />
                    <span className="mt-1 text-base md:text-xl">Life-changing programs for weight loss</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Background image */}
            <GatsbyImage
              image={imageSources}
              objectFit="cover"
              alt="egg background"
              loading="eager"
              className="w-full md:h-full !absolute top-0"
            />
          </section>

          <section className="md:w-1/2 max-w-xl px-4 mx-auto pb-56 md:pb-4 pt-8 md:pt-24">
            <div className="flex flex-col items-center md:items-start">
              <div className="text-center md:text-left">
                <h4 className="text-[1.5rem] md:text-3xl font-medium">Start your free 7-day trial</h4>
                <p className="text-[1.063rem] mt-1.5">No commitment. Cancel anytime.</p>
              </div>

              <div className="flex flex-col mt-[18px] mx-0">
                <div className="flex flex-row items-end justify-between mt-1.5">
                  <span className=" font-medium">PRICE AFTER TRIAL</span>

                  <div className="bg-green p-1 pb-0.5 px-3 text-white text-center text-base rounded-full">
                    45% OFF
                  </div>
                </div>

                <hr className="w-full border-b border-t-0 opacity-40 mt-2"></hr>

                <div className="flex flex-row justify-between mt-2">
                  <span>Original cost per month</span>
                  <span>$9.92</span>
                </div>

                <div className="flex flex-row justify-between mt-1.5">
                  <span>Discount (45% off)</span>
                  <span>-$3.75</span>
                </div>

                <hr className="w-full border-b border-t-0 opacity-40 mt-2"></hr>

                <div className="flex flex-row justify-between mt-2">
                  <span>Total after discount</span>
                  <span>$4.5</span>
                </div>

                <div className="text-[16px] mt-1.5">US$54 automatically billed after trial.</div>

                <span className="mt-8 font-medium">PRICE TODAY</span>

                <hr className="w-full border-b border-t-0 opacity-40 mt-2"></hr>

                <div className="flex flex-row justify-between mt-2">
                  <span>7-day free trial</span>
                  <div>
                    <span className="font-medium">$0</span>
                  </div>
                </div>

                <div className="flex flex-row items-center space-x-2 mt-3.5 text-base">
                  <svg
                    width="1em"
                    height="1em"
                    viewBox="0 0 17 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="shrink-0 mt-1"
                  >
                    <path
                      d="M8.5 0l2.296 6.204L17 8.5l-6.204 2.296L8.5 17l-2.296-6.204L0 8.5l6.204-2.296L8.5 0z"
                      fill="#4FAE64"
                    />
                  </svg>
                  <span>
                    You'll have 1 month to see how <span className="whitespace-nowrap">Diet Doctor</span>{" "}
                    creates <span className="whitespace-nowrap">sustainable results</span>
                  </span>
                </div>
              </div>
            </div>

            {/* Error box */}
            <PaymentError />

            <section className="mt-8 max-w-md">
              <h4 className="text-2xl self-start font-normal">Provide your details</h4>

              <h5 className="text-lg mt-2 mb-2 self-start font-normal">Choose a payment method:</h5>

              {/* Select payment method */}
              <PaymentMethodSelect
                debitCreditText={tt.debitCredit}
                paypalText={tt.paypal}
                scrollToRef={paymentDetailsRef}
              />

              {/* Payment method input, PayPal & Stripe */}
              <div ref={paymentDetailsRef}>
                {user?.userId && (
                  <>
                    <StripeSubscribeSection
                      discount={true}
                      disclaimer="You will not be charged until your free trial ends. If you don't cancel, you will be charged US$54 for a 1 year subscription that will renew at US$119, billed annually. You can cancel anytime and pay nothing."
                    />
                    <PayPalSubscribeSection
                      discount={true}
                      disclaimer="You will not be charged until your free trial ends. If you don't cancel, you will be charged US$54 for a 1 year subscription that will renew at US$119, billed annually. You can cancel anytime and pay nothing."
                    />
                  </>
                )}
              </div>
            </section>
          </section>
        </main>
      </PaymentProvider>
    </>
  );
};

const WhiteCheckMark = () => (
  <svg width={22} height={22} fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx={10.901} cy={10.901} r={10.901} fill="#fff" />
    <path
      d="M16.424 7.63L8.83 15.226l-3.452-3.452"
      stroke="#000"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

function useTranslations() {
  return {
    paymentSEOTitle: t({
      id: "SignUpFlow.paymentSEOTitle",
      message: "Payment",
    }),
    paymentTitle: t({
      id: "SignUpFlow.paymentTitle",
      message: "Try DD+ for free",
    }),
    paymentText: t({
      id: "SignUpFlow.paymentText",
      message: "No commitment. Cancel anytime.",
    }),
    debitCredit: t({
      id: "SignUpFlow.debitCredit",
      message: "Debit/credit",
    }),
    paypal: t({
      id: "SignUpFlow.paypal",
      message: "PayPal",
    }),
  };
}

export default React.memo(SubscriptionPaymentPageV3);
